/* eslint-disable max-len */
import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ColorTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { mainColors } from '../../helpers/colors';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CCollapse,
  CRow,
  CCol,
  CFormInput
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash, cilFilter, cilX, cilSearch } from '@coreui/icons';
import { deleteColorRequestAction } from '../../store/color/colorActions';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const columns = [
  { id: 'index', label: '#', sortable: false },
  { id: 'ColorCode', label: 'colorCode', sortable: true, filterable: true },
  { id: 'ColorName', label: 'colorName', sortable: true, filterable: true },
  { id: 'ColorDescription', label: 'description', sortable: true, filterable: true },
  { id: 'actions', label: 'Actions', sortable: false }
];

function ColorTable(props) {
  const { 
    colorList,
    colorData,
    changeSortType,
    changeSortKey,
    colorHistorySortData: _colorHistorySortData,
    deleteColorRequest
  } = props;
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(columns.map(c => c.id), 'ColorTable');
  
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    ColorCode: '',
    ColorName: '',
    ColorDescription: ''
  });

  useEffect(() => {
    if (colorList && Array.isArray(colorList)) {
      setTableData(colorList);
    }
  }, [colorList]);

  useEffect(() => {
    setPage(1);
  }, [filters, colorList]);

  const handleEdit = (item) => {
    console.log('Editing color:', item);
    navigate(`/color/edit/${item.ColorId}`, {
      state: { color: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          ColorId: deleteModal.item.ColorId
        };

        console.log('Deleting color:', deleteData);
        deleteColorRequest({ data: deleteData });
      } else {
        console.error('User session not found');
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    scrollToTableContainer();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      ColorCode: '',
      ColorName: '',
      ColorDescription: ''
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    if (changeSortType) changeSortType(newOrder);
    if (changeSortKey) changeSortKey(property);
  };

  function scrollToTableContainer() {
    const tableContainer = document.querySelector('.color-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const filteredData = useMemo(() => {
    return tableData.filter((item) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key]?.toLowerCase() || '';
        if (!filterValue) return true;
        const itemValue = item[key]?.toString().toLowerCase() || '';
        return itemValue.includes(filterValue);
      });
    });
  }, [tableData, filters]);

  const getSortedData = () => {
    const stabilizedData = stableSort(filteredData, getComparator(order, orderBy));
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return stabilizedData.slice(startIndex, endIndex);
  };

  const totalRows = filteredData.length;

  const totalPages = Math.ceil(totalRows / rowsPerPage);
  
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem 
        key={i} 
        active={i === page} 
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }
  
  const activeFilterCount = Object.values(filters).filter((value) => value.trim() !== '').length;

  return (
    <div className="color-table-container">
      <CCard className="mb-4">
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? 'primary' : 'outline-primary'}
                  variant={showFilters ? 'solid' : 'outline'}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                  {tableData.length !== totalRows && (
                    <span className="filtered-info ms-1">
                      ({tableData.length} {t('totalRecords')})
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {columns.filter((col) => col.filterable).map((column) => (
                  <CCol key={column.id} xs={12} sm={6} md={4} lg={4}>
                    <div className="filter-field">
                      <label className="filter-label">{t(column.label)}</label>
                      <div className="filter-input-wrapper">
                        <CIcon icon={cilSearch} className="filter-input-icon" />
                        <CFormInput
                          type="text"
                          placeholder={`${t('search')} ${t(column.label).toLowerCase()}...`}
                          value={filters[column.id]}
                          onChange={(e) => handleFilterChange(column.id, e.target.value)}
                          className="filter-input"
                          size="sm"
                        />
                        {filters[column.id] && (
                          <CButton
                            color="link"
                            size="sm"
                            className="filter-clear-btn"
                            onClick={() => handleFilterChange(column.id, '')}
                          >
                            <CIcon icon={cilX} size="sm" />
                          </CButton>
                        )}
                      </div>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell 
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }}
                    >
                      {t(column.label)}
                      {column.sortable && orderBy === column.id && (
                        <CIcon
                          icon={order === 'asc' ? cilArrowTop : cilArrowBottom}
                          size="sm"
                          className="ms-1"
                        />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.ColorId || index}>
                    <CTableDataCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="info" shape="rounded-pill">
                        {item.ColorCode}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {item.ColorName}
                    </CTableDataCell>
                    <CTableDataCell>
                      <span title={item.ColorDescription}>
                        {item.ColorDescription?.length > 50 
                          ? `${item.ColorDescription.substring(0, 50)}...`
                          : item.ColorDescription
                        }
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('edit')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleEdit(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilPencil}
                              size="sm"
                              style={{ color: mainColors.primary }}
                            />
                          </div>
                        </CTooltip>
                        
                        <CTooltip content={t('delete')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleDeleteClick(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilTrash}
                              size="sm"
                              style={{ color: '#dc3545' }}
                            />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center py-4">
                    <div className="no-data-message">
                      {colorData?.Result?.ErrorCode !== 0 ? (
                        <div className="loading-message">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          {t('loadingData')}
                        </div>
                      ) : activeFilterCount > 0 ? (
                        <div className="no-results-message">
                          <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                          <div>{t('noResultsFound')}</div>
                          <CButton
                            color="link"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-2"
                          >
                            {t('clearFiltersToSeeAll')}
                          </CButton>
                        </div>
                      ) : (
                        <div className="text-muted">{t('noDataFound')}</div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select 
                  className="form-select form-select-sm" 
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              
              <CPagination>
                        <CPaginationItem 
          aria-label={t('previous')} 
          disabled={page === 1}
          onClick={() => handleChangePage(page - 1)}
        >
          <span aria-hidden="true">&laquo;</span>
        </CPaginationItem>
                {paginationItems}
                        <CPaginationItem 
          aria-label={t('next')} 
          disabled={page === totalPages}
          onClick={() => handleChangePage(page + 1)}
        >
          <span aria-hidden="true">&raquo;</span>
        </CPaginationItem>
              </CPagination>
              
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('confirmDelete')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteColorStatus')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('colorName')}:</strong> {deleteModal.item.ColorName}<br/>
              <strong>{t('colorCode')}:</strong> {deleteModal.item.ColorCode}<br/>
              <strong>{t('description')}:</strong> {deleteModal.item.ColorDescription}<br/>
              <strong>{t('id')}:</strong> {deleteModal.item.ColorId}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('thisActionCannotBeUndone')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>
            {t('cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            {t('delete')}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    colorList: state.colorReducer?.colorList || [],
    colorData: state.colorReducer?.colorData || null,
    colorHistorySortData: state.colorReducer?.colorHistorySortData || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteColorRequest: (payload) => {
      dispatch(deleteColorRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ColorTable);